<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-call-transcripts.WEB.T2 / WEB.T3 — one turn of the reader: the
// clock (seeks to the turn), the speaker in their colour, and the text as
// word spans (`data-t` / `data-e`, as in the desktop reader). A click on a
// word seeks to that word; anywhere else in the turn seeks to its start.
// Its own component so a playhead tick re-renders only the turns whose
// "now" / current word / find marks changed.
import { computed } from 'vue';
import { formatClock, tokenPieces } from './kaptionCallTranscriptModel';

const props = defineProps({
  row: { type: Object, required: true },
  speakerName: { type: String, default: '' },
  tone: { type: Object, required: true },
  isNow: { type: Boolean, default: false },
  currentWord: { type: Number, default: -1 },
  // Find marks over the turn's text: [[from, to, isCurrent]].
  ranges: { type: Array, default: () => [] },
  hasCurrentMatch: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits(['seek']);

const tokens = computed(() =>
  props.row.tokens.map((token, index) => ({
    ...token,
    index,
    pieces: props.ranges.length
      ? tokenPieces(token, props.ranges)
      : [{ text: token.text, mark: false }],
  }))
);

const onClick = event => {
  const word = event.target?.closest?.('[data-t]');
  emit('seek', word ? Number(word.dataset.t) : props.row.turn.start);
};
</script>

<template>
  <li
    data-testid="kaption-call-transcript-turn"
    :data-now="isNow ? '' : undefined"
    :data-speaker="row.turn.speaker"
    class="grid grid-cols-[3.25rem_minmax(0,1fr)] gap-x-2.5 rounded-lg py-1.5 pr-2.5 transition-colors"
    :class="[
      isNow ? 'bg-n-alpha-2 shadow-[inset_2px_0_0_#F97316]' : '',
      hasCurrentMatch ? 'ring-1 ring-n-amber-8' : '',
    ]"
  >
    <button
      type="button"
      data-testid="kaption-call-transcript-clock"
      class="pt-0.5 text-right font-mono text-xs tabular-nums text-n-slate-10 hover:text-n-slate-12"
      @click="emit('seek', row.turn.start)"
    >
      {{ formatClock(row.turn.start) }}
    </button>
    <div class="flex min-w-0 flex-col gap-0.5">
      <span class="text-xs font-semibold" :class="tone.text">
        {{ speakerName }}
      </span>
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
      <p
        class="mb-0 cursor-pointer leading-relaxed text-n-slate-12"
        :class="compact ? 'text-[13px]' : 'text-sm'"
        @click="onClick"
      >
        <template v-for="token in tokens" :key="token.index">
          <span
            v-if="token.start !== undefined"
            data-testid="kaption-call-transcript-word"
            :data-t="token.start"
            :data-e="token.end"
            :data-approx="token.approx ? '1' : undefined"
            :data-now-word="
              isNow && currentWord === token.index ? '' : undefined
            "
            class="-mx-[0.12em] rounded-[3px] px-[0.12em] py-px hover:bg-n-alpha-2"
            :class="{
              'bg-[#F97316]/20 text-n-slate-12 hover:bg-[#F97316]/25':
                isNow && currentWord === token.index,
            }"
          >
            <template v-for="(piece, p) in token.pieces" :key="p">
              <mark
                v-if="piece.mark"
                data-testid="kaption-call-transcript-mark"
                :data-current="piece.current ? '' : undefined"
                class="rounded-[2px] text-n-slate-12"
                :class="piece.current ? 'bg-n-amber-7' : 'bg-n-amber-4'"
                >{{ piece.text }}</mark
              ><template v-else>{{ piece.text }}</template>
            </template>
          </span>
          <span v-else>
            <template v-for="(piece, p) in token.pieces" :key="p">
              <mark
                v-if="piece.mark"
                data-testid="kaption-call-transcript-mark"
                :data-current="piece.current ? '' : undefined"
                class="rounded-[2px] text-n-slate-12"
                :class="piece.current ? 'bg-n-amber-7' : 'bg-n-amber-4'"
                >{{ piece.text }}</mark
              ><template v-else>{{ piece.text }}</template>
            </template> </span
          >{{ ' ' }}
        </template>
      </p>
    </div>
  </li>
</template>
